import { call, put, takeLatest, select } from "redux-saga/effects";
import { axiosRestInstance } from "../../../common/axios";
import { AxiosResponse } from "axios";
import {
  FormFieldType,
  SalesNoteFormDetailType,
  NoteType,
} from "../../slices/notes/Types";
import { notesSliceAction } from "../../slices/notes";
import {
  addFormSalesNoteSagaActionPayloadType,
  getNotesListSagaAction,
  notesSagaActionTypes,
  updateFormSalesNoteSagaActionPayloadType,
} from "./saga-actions";
import { RootStateType } from "../..";

type getFormFieldsSalesNoteAPIResponseType = {
  success: boolean;
  message: string;
  data: FormFieldType[];
};
const getFormFieldsSalesNoteAPI = async () => {
  let response = axiosRestInstance
    .get<AxiosResponse<getFormFieldsSalesNoteAPIResponseType>>(
      "/formField/UserPartnerNote"
    )
    .then((res) => res.data);
  return response;
};

type addFormSalesNoteAPIResponseType = {
  success: boolean;
  message: string;
};
const addFormSalesNoteAPI = async (
  payload: addFormSalesNoteSagaActionPayloadType
) => {
  let response = axiosRestInstance
    .post<AxiosResponse<addFormSalesNoteAPIResponseType>>("/form", payload)
    .then((res) => res.data);
  return response;
};

type getSalesNoteFormDetailAPIResponseType = {
  success: true;
  message: string;
  data: SalesNoteFormDetailType[];
};
const getSalesNoteFormDetailAPI = async (partnerID: number) => {
  let response = axiosRestInstance
    .get<AxiosResponse<getSalesNoteFormDetailAPIResponseType>>(
      `/form/submissions/filter/UserPartnerNote?partner_id=${partnerID}`
    )
    .then((res) => res.data);
  return response;
};

type updateFormSalesNoteAPIResponseType = {
  success: boolean;
  message: string;
};
const updateFormSalesNoteAPI = async (
  payload: updateFormSalesNoteSagaActionPayloadType
) => {
  let response = axiosRestInstance
    .put<AxiosResponse<updateFormSalesNoteAPIResponseType>>(
      `/form/update/${payload.id}`,
      payload
    )
    .then((res) => res.data);
  return response;
};

// GET Notes List
type getNotesListAPIResponseType = {
  success: boolean;
  message: string;
  data: NoteType[];
};
const getNotesListAPI = async (partnerID: number) => {
  const response = await axiosRestInstance
    .get<AxiosResponse<getNotesListAPIResponseType>>(`/notes/${partnerID}`)
    .then((res) => res.data);
  return response;
};

// ADD Note
type addNoteAPIResponseType = {
  success: boolean;
  message: string;
  data: NoteType;
};
const addNoteAPI = async (payload: NoteType) => {
  const response = await axiosRestInstance
    .post<AxiosResponse<addNoteAPIResponseType>>("/notes", payload)
    .then((res) => res.data);
  return response;
};

// UPDATE Note
type updateNoteAPIResponseType = {
  success: boolean;
  message: string;
};
const updateNoteAPI = async (payload: NoteType) => {
  const response = await axiosRestInstance
    .put<AxiosResponse<updateNoteAPIResponseType>>(
      `/notes/${payload.ID}`,
      payload
    )
    .then((res) => res.data);
  return response;
};

// DELETE Note
type deleteNoteAPIResponseType = {
  success: boolean;
  message: string;
};
const deleteNoteAPI = async (noteID: number) => {
  const response = await axiosRestInstance
    .delete<AxiosResponse<deleteNoteAPIResponseType>>(`/notes/${noteID}`)
    .then((res) => res.data);
  return response;
};

// ----------------------- API Above

function* getFormFieldsSalesNoteSaga(action: { type: string }) {
  try {
    yield put(notesSliceAction.setFormFieldsSalesNoteLoading(true));
    let response: getFormFieldsSalesNoteAPIResponseType = yield call(
      getFormFieldsSalesNoteAPI
    );
    yield put(notesSliceAction.setFormFieldsSalesNote(response.data || []));
  } catch (e) {
    console.log(e);
  } finally {
    yield put(notesSliceAction.setFormFieldsSalesNoteLoading(false));
  }
}

function* addFormSalesNoteSaga(action: {
  type: string;
  payload: addFormSalesNoteSagaActionPayloadType;
}) {
  try {
    yield put(notesSliceAction.setAddFormSalesNoteLoading(true));
    let response: addFormSalesNoteAPIResponseType = yield call(
      addFormSalesNoteAPI,
      action.payload
    );
    yield put(notesSliceAction.setAddFormSalesNoteStatus(true));
    yield put(notesSliceAction.setAddFormSalesNoteMessage(response.message));
  } catch (e: any) {
    yield put(notesSliceAction.setAddFormSalesNoteStatus(false));
    yield put(
      notesSliceAction.setAddFormSalesNoteMessage(
        e?.response?.data?.message || "Something went wrong"
      )
    );
    console.log(e);
  } finally {
    yield put(notesSliceAction.setAddFormSalesNoteLoading(false));
  }
}

function* getSalesNoteFormDetailSaga(action: {
  type: string;
  partnerID: number;
}) {
  try {
    yield put(notesSliceAction.setSalesNoteFormDetailLoading(true));
    let response: getSalesNoteFormDetailAPIResponseType = yield call(
      getSalesNoteFormDetailAPI,
      action.partnerID
    );
    yield put(
      notesSliceAction.setSalesNoteFormDetail(response.data?.[0] || null)
    );
  } catch (e) {
    yield put(notesSliceAction.setSalesNoteFormDetail(null));
    console.log(e);
  } finally {
    yield put(notesSliceAction.setSalesNoteFormDetailLoading(false));
  }
}

function* updateFormSalesNoteSaga(action: {
  type: string;
  payload: updateFormSalesNoteSagaActionPayloadType;
}) {
  try {
    yield put(notesSliceAction.setAddFormSalesNoteLoading(true));
    let response: updateFormSalesNoteAPIResponseType = yield call(
      updateFormSalesNoteAPI,
      action.payload
    );
    yield put(notesSliceAction.setAddFormSalesNoteStatus(true));
    yield put(notesSliceAction.setAddFormSalesNoteMessage(response.message));
  } catch (e: any) {
    yield put(notesSliceAction.setAddFormSalesNoteStatus(false));
    yield put(
      notesSliceAction.setAddFormSalesNoteMessage(
        e?.response?.data?.message || "Something went wrong"
      )
    );
    console.log(e);
  } finally {
    yield put(notesSliceAction.setAddFormSalesNoteLoading(false));
  }
}

function* getNotesListSaga(action: { type: string; partnerID: number }) {
  try {
    yield put(notesSliceAction.setNotesListLoading(true));
    const response: getNotesListAPIResponseType = yield call(
      getNotesListAPI,
      action.partnerID
    );
    yield put(notesSliceAction.setNotesList(response.data || []));
  } catch (e) {
    yield put(notesSliceAction.setNotesList([]));
    console.log(e);
  } finally {
    yield put(notesSliceAction.setNotesListLoading(false));
  }
}

function* addNoteSaga(action: { type: string; payload: NoteType }) {
  try {
    yield put(notesSliceAction.setAddNoteLoading(true));
    const response: addNoteAPIResponseType = yield call(
      addNoteAPI,
      action.payload
    );
    yield put(getNotesListSagaAction(action.payload.partner_id));
  } catch (e) {
    console.log(e);
  } finally {
    yield put(notesSliceAction.setAddNoteLoading(false));
  }
}

function* updateNoteSaga(action: { type: string; payload: NoteType }) {
  try {
    yield put(notesSliceAction.setUpdateNoteLoading(true));
    yield call(updateNoteAPI, action.payload);
    yield put(notesSliceAction.setUpdatedNote(action.payload));
  } catch (e) {
    console.log(e);
  } finally {
    yield put(notesSliceAction.setUpdateNoteLoading(false));
  }
}

function* deleteNoteSaga(action: {
  type: string;
  payload: {
    noteID: number;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  };
}) {
  try {
    yield put(notesSliceAction.setDeleteNoteLoading(true));
    yield call(deleteNoteAPI, action.payload.noteID);
    yield put(notesSliceAction.setDeletedNote(action.payload.noteID));
    action.payload.setModalOpen(false);
  } catch (e) {
    console.log(e);
  } finally {
    yield put(notesSliceAction.setDeleteNoteLoading(false));
  }
}

function* notesSaga() {
  yield takeLatest(
    notesSagaActionTypes.callGetFormFieldsSalesNoteSaga,
    getFormFieldsSalesNoteSaga
  );
  yield takeLatest(
    notesSagaActionTypes.callAddFormSalesNoteSaga,
    addFormSalesNoteSaga
  );
  yield takeLatest(
    notesSagaActionTypes.callGetSalesNoteFormDetailSaga,
    getSalesNoteFormDetailSaga
  );
  yield takeLatest(
    notesSagaActionTypes.callUpdateFormSalesNoteSaga,
    updateFormSalesNoteSaga
  );
  yield takeLatest(notesSagaActionTypes.callGetNotesListSaga, getNotesListSaga);
  yield takeLatest(notesSagaActionTypes.callAddNoteSaga, addNoteSaga);
  yield takeLatest(notesSagaActionTypes.callUpdateNoteSaga, updateNoteSaga);
  yield takeLatest(notesSagaActionTypes.callDeleteNoteSaga, deleteNoteSaga);
}

export default notesSaga;
